import { Expose } from 'class-transformer';

import { Organization } from './Organization';
import { RoleGroup } from './RoleGroup';
import { Model } from './Model';

export enum AccountOnboardingState {
	PendingApproval       = 'PENDING_APPROVAL',
	PillarReviewRequired  = 'PILLAR_REVIEW_REQUIRED',
	FloorParticipationRequired = 'FLOOR_PARTICIPATION_REQUIRED',
	Active                = 'ACTIVE',
	Suspended             = 'SUSPENDED',
	Deactivated           = 'DEACTIVATED',
}

//
// UNCONFIRMED - User has been created but not confirmed.
// CONFIRMED - User has been confirmed.
// ARCHIVED - User is no longer active.
// COMPROMISED - User is disabled due to a potential security threat.
// UNKNOWN - User status is not known.
// RESET_REQUIRED - User is confirmed, but the user must request a code and reset his or her password before he or she can sign in.
// FORCE_CHANGE_PASSWORD - The user is confirmed and the user can sign in using a temporary password, but on first sign-in, the user must change his or her password to a new value before doing anything else.
//

export enum USER_STATUS {
	UNCONFIRMED = 'UNCONFIRMED',
	CONFIRMED = 'CONFIRMED',
	ARCHIVED = 'ARCHIVED',
	COMPROMISED = 'COMPROMISED',
	UNKNOWN = 'UNKNOWN',
	RESET_REQUIRED = 'RESET_REQUIRED',
	FORCE_CHANGE_PASSWORD = 'FORCE_CHANGE_PASSWORD',
}

export class User extends Model {

	private _username: string | null = null;
	private _firstName: string | null = null;
	private _lastName: string | null = null;
	private _sortName: string = '';
	private _groups: Set<string> = new Set<string>();
	private _fullName: string = '';
	private _organizationId: string | null = null;
	private _organization: Organization | null = null;
	private _userImageUrl: string | null = null;
	private _locale: string | null = null;
	private _timezone: string | null = null;
	private _authTime: Date | null = null;
	private _expireTime = 0;
	private _status: USER_STATUS = USER_STATUS.UNCONFIRMED;
	private _enabled = false;
	private _accountState: AccountOnboardingState = AccountOnboardingState.PendingApproval;
	private _trustLevel = 0;

	constructor(rawUser: any) {
		super(rawUser);

		if (!rawUser) {
			return;
		}

		if (rawUser.idToken?.payload) {
			// Shape 1: CognitoUserSession — produced by amazon-cognito-identity-js after login/refresh.
			// session.idToken.payload contains the full JWT claims.
			this.fromCognitoIdPayload(rawUser.idToken.payload);
			this._authTime = rawUser.idToken.payload.auth_time
				? new Date(rawUser.idToken.payload.auth_time * 1000)
				: null;
			this._expireTime = rawUser.idToken.payload.exp ?? 0;

		} else if (rawUser.accessToken) {
			// Shape 2: { accessToken: decodedPayload } — produced by group.guard.ts from the
			// verified JWT token attached to request.user by Passport.
			const payload = rawUser.accessToken;
			this._username = payload.username ?? payload.email ?? null;
			this.fromGroups(payload['cognito:groups']);

		} else if (rawUser.Attributes || rawUser.UserAttributes) {
			// Shape 3: Cognito Admin API user — produced by adminListUsers / adminGetUser.
			// Attributes is an array of { Name, Value } pairs.
			const attrs: any[] = rawUser.UserAttributes ?? rawUser.Attributes;
			this._id = this.findAttribute(attrs, 'sub') ?? '';
			if (this._id) this._isNew = false;
			this._firstName = this.findAttribute(attrs, 'given_name') ?? '';
			this._lastName = this.findAttribute(attrs, 'family_name') ?? '';
			this._username = this.findAttribute(attrs, 'email') ?? null;
			this._organizationId = this.findAttribute(attrs, 'custom:organizationId') ?? null;
			this._locale = this.findAttribute(attrs, 'locale') ?? null;
			this._timezone = this.findAttribute(attrs, 'zoneinfo') ?? null;
			this._enabled = rawUser.Enabled ?? false;
			this._status = rawUser.UserStatus ?? USER_STATUS.UNCONFIRMED;
			this.fromGroups(rawUser['cognito:groups']);

		} else if (rawUser.sub) {
			// Shape 4: Flat Cognito token payload — idToken.payload extracted directly (e.g. stored
			// in sessionStorage, or decoded by a JWT strategy and placed on request.user without the
			// accessToken wrapper).
			this.fromCognitoIdPayload(rawUser);
			this._authTime = rawUser.auth_time ? new Date(rawUser.auth_time * 1000) : null;
			this._expireTime = rawUser.exp ?? 0;

		} else {
			// Shape 5: Plain serialized user — produced by our own backend API responses and
			// by re-hydrating from originalJson. Uses camelCase property names.
			this._id = rawUser.id ?? null;
			if (this._id) this._isNew = false;
			this._username = rawUser.email ?? rawUser.username ?? null;
			this._firstName = rawUser.firstName ?? null;
			this._lastName = rawUser.lastName ?? null;
			this._organizationId = rawUser.organizationId ?? null;
			this._locale = rawUser.locale ?? null;
			this._timezone = rawUser.timezone ?? null;
			this._enabled = rawUser.enabled ?? false;
			this._status = rawUser.status ?? USER_STATUS.UNCONFIRMED;
			this._accountState = rawUser.accountState ?? AccountOnboardingState.PendingApproval;
			this.fromGroups(rawUser.groups);
		}

		this._fullName = `${this._firstName ?? ''} ${this._lastName ?? ''}`.trim();
		this._sortName = `${this._lastName ?? ''}_${this._firstName ?? ''}`.toLowerCase();
		this.originalJson = rawUser;
	}

	// Populates core identity fields from a flat Cognito ID token payload (shape 1 & 4).
	private fromCognitoIdPayload(payload: any): void {
		this._id = payload.sub ?? null;
		if (this._id) this._isNew = false;
		this._username = payload.email ?? null;
		this._firstName = payload.given_name ?? '';
		this._lastName = payload.family_name ?? '';
		this._organizationId = payload['custom:organizationId'] ?? null;
		this._locale = payload.locale ?? null;
		this._timezone = payload.zoneinfo ?? null;
		this._enabled = payload.enabled ?? true;
		this._status = USER_STATUS.CONFIRMED;
		this.fromGroups(payload['cognito:groups']);
	}

	// Populates the groups set from an array of group name strings (null-safe).
	private fromGroups(groups: string[] | null | undefined): void {
		if (!groups) return;
		groups.forEach((g: string) => this._groups.add(g));
	}

	// Finds a Cognito attribute by Name in the { Name, Value }[] array.
	private findAttribute(attributes: { Name: string; Value: string }[], name: string): string | null {
		return attributes.find((a) => a.Name === name)?.Value ?? null;
	}

	addGroup(groupName: string): void {
		this._groups.add(groupName);
	}

	belongsToGroup(groupName: string): boolean {
		return this._groups.has(groupName);
	}

	removeGroup(groupName: string): boolean {
		return this._groups.delete(groupName);
	}

	isAdmin(): boolean {
		return this._groups.has(RoleGroup.ADMIN_GROUP_NAME);
	}

	@Expose()
	set firstName(v: string | null) {
		this._firstName = v;
		this._fullName = `${v ?? ''} ${this._lastName ?? ''}`.trim();
		this._sortName = `${this._lastName ?? ''}_${v ?? ''}`.toLowerCase();
	}
	get firstName(): string | null { return this._firstName; }

	@Expose()
	set username(v: string | null) { this._username = v; }
	get username(): string | null { return this._username; }

	@Expose()
	set lastName(v: string | null) {
		this._lastName = v;
		this._fullName = `${this._firstName ?? ''} ${v ?? ''}`.trim();
		this._sortName = `${v ?? ''}_${this._firstName ?? ''}`.toLowerCase();
	}
	get lastName(): string | null { return this._lastName; }

	@Expose()
	set groups(newGroups: string[]) {
		this._groups = new Set<string>();
		newGroups?.forEach((g) => this._groups.add(g));
	}
	get groups(): string[] { return this._groups ? [...this._groups] : []; }

	@Expose()
	get userId(): string | null { return this._id; }

	get fullName(): string { return this._fullName; }

	@Expose()
	set userImageUrl(v: string | null) { this._userImageUrl = v; }
	get userImageUrl(): string | null { return this._userImageUrl; }

	@Expose()
	set organizationId(v: string | null) { this._organizationId = v; }
	get organizationId(): string | null { return this._organizationId; }

	@Expose()
	set locale(v: string | null) { this._locale = v; }
	get locale(): string | null { return this._locale; }

	@Expose()
	set timezone(v: string | null) { this._timezone = v; }
	get timezone(): string | null { return this._timezone; }

	@Expose()
	set authTime(v: Date | string | null) {
		this._authTime = v ? new Date(v as string) : null;
	}
	get authTime(): Date | null { return this._authTime; }

	@Expose()
	set organization(v: Organization | null) { this._organization = v; }
	get organization(): Organization | null { return this._organization; }

	@Expose()
	set status(v: string) { this._status = v as USER_STATUS; }
	get status(): string { return this._status; }

	get sortName(): string { return this._sortName; }

	@Expose()
	set enabled(v: boolean) { this._enabled = v; }
	get enabled(): boolean { return this._enabled; }

	get expireTime(): number { return this._expireTime; }

	@Expose()
	set accountState(v: AccountOnboardingState) { this._accountState = v; }
	get accountState(): AccountOnboardingState { return this._accountState; }

	get isActiveMember(): boolean {
		return this._accountState === AccountOnboardingState.Active;
	}

	get isPillarReviewRequired(): boolean {
		return this._accountState === AccountOnboardingState.PillarReviewRequired;
	}

	set trustLevel(v: number) { this._trustLevel = v; }
	get trustLevel(): number { return this._trustLevel; }
}
